import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs';
import { InputComponent } from '../../input/input.component';
import { SearchIconComponent, TagCrossIconComponent } from '@kuiper/icons';
import { SearchEnum } from '../../../../core/common/enums/search.enum';

@Component({
  selector: 'lib-url-search',
  imports: [
    InputComponent,
    ReactiveFormsModule,
    SearchIconComponent,
    TagCrossIconComponent,
  ],
  templateUrl: './url-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UrlSearchComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  placeholder = input<string>('');
  width = input<string>(SearchEnum.searchInputWidth);
  searchValue = output<string>();

  searchForm = new FormGroup({
    search: new FormControl<string>('', { nonNullable: true }),
  });

  private get control(): FormControl<string> {
    return this.searchForm.get('search') as FormControl<string>;
  }

  // Convert filtered valueChanges to a signal — undefined until first emission
  private readonly searchSignal = toSignal(
    this.control.valueChanges.pipe(
      debounceTime(300),
      map(v => v.trim()),
      filter(v => v.length === 0 || v.length >= SearchEnum.minSearchLength),
      distinctUntilChanged()
    ),
    { initialValue: this.route.snapshot.queryParams[SearchEnum.keyword] ?? '' }
  );

  constructor() {
    // Single effect: writes to URL and emits to parent on every valid change
    effect(() => {
      const query = this.searchSignal();
      this.syncToUrl(query);
      this.searchValue.emit(query);
    });
  }

  ngOnInit(): void {
    // Seed control from current URL (deep link / page refresh) — no emission
    const initial = this.route.snapshot.queryParams[SearchEnum.keyword] ?? '';
    this.control.setValue(initial, { emitEvent: false });
  }

  clear(): void {
    this.control.setValue('');
  }

  private syncToUrl(query: string): void {
    const urlTree = this.router.parseUrl(this.router.url);
    if (query) {
      urlTree.queryParams[SearchEnum.keyword] = query;
    } else {
      delete urlTree.queryParams[SearchEnum.keyword];
    }
    this.router.navigateByUrl(urlTree);
  }
}
